import React, { useRef, useState, useMemo, useEffect } from 'react';
import { PlanSheet } from '@/app/types/plan';
import { 
  PlacedBlock, 
  SocketConnection, 
  TextAnnotation, 
  PlacedLabel, 
  LabelTemplate, 
  BlockSystem,
  PipeType,
  BackgroundSettings,
  ViewDisplaySettings,
  ConnectionMarker,
  ViewState
} from '@/app/types/cad';
import { BlockSystemCanvas } from '../BlockSystemCanvas';
import { Printer, ZoomIn, ZoomOut, X, Focus, Lock, Unlock, Download, Grid } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { DynamicTitleBlock } from './DynamicTitleBlock';
import { DebugGridOverlay } from './DebugGridOverlay';
import { getFahnerTemplate } from './fahnerTemplate';
import { TitleBlockToolbar, GridAction } from './TitleBlockToolbar';
import { isoToScreen } from '@/app/utils/isometric';
import { exportPlanToDxf } from '@/app/utils/dxfExport';
import { toast } from 'sonner';
import { TitleBlockCell, TitleBlockCellStyles, TitleBlockTemplate } from '@/app/types/titleblock';
import { insertRow, deleteRow, insertCol, deleteCol, mergeCells, mergeSelectedCells, splitCell, setBorderMode } from '@/app/utils/titleBlockOps';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { TitleBlockStorage } from '@/app/utils/titleBlockStorage';
import { Save, FolderOpen, Trash2 } from 'lucide-react';

// Component for editing plan layout
interface PlanLayoutEditorProps {
  plan: PlanSheet;
  blocks: PlacedBlock[];
  connections: SocketConnection[];
  textAnnotations: TextAnnotation[];
  placedLabels: PlacedLabel[];
  labelTemplates: LabelTemplate[];
  systems: BlockSystem[];
  systemColors: Map<string, string>;
  backgroundSettings: BackgroundSettings;
  pipeTypes?: PipeType[];
  viewDisplaySettings?: ViewDisplaySettings;
  connectionMarkers?: ConnectionMarker[];
  projectName?: string;
  onUpdatePlan: (id: string, updates: Partial<PlanSheet>) => void;
  onClose?: () => void;
  // Project-level templates persistence
  projectTemplates?: TitleBlockTemplate[];
  onSaveProjectTemplate?: (template: TitleBlockTemplate) => void;
  onDeleteProjectTemplate?: (id: string) => void;
}

import { useLanguage } from '@/app/contexts/LanguageContext';

export function PlanLayoutEditor({
  plan,
  blocks,
  connections,
  textAnnotations,
  placedLabels,
  labelTemplates,
  systems,
  systemColors,
  backgroundSettings,
  pipeTypes,
  viewDisplaySettings,
  connectionMarkers,
  projectName,
  onUpdatePlan,
  onClose,
  projectTemplates = [],
  onSaveProjectTemplate,
  onDeleteProjectTemplate
}: PlanLayoutEditorProps) {
  const { t } = useLanguage();
  const [zoom, setZoom] = useState(0.8); // Screen zoom factor
  const [pan, setPan] = useState({ x: 0, y: 0 }); // Screen pan offset
  
  // Drag State for Pan / Move Title Block
  const dragModeRef = useRef<'pan' | 'title' | null>(null);
  const lastMouseRef = useRef({ x: 0, y: 0 });

  // Title Block Editing State
  const [isEditingTitleBlock, setIsEditingTitleBlock] = useState(false);
  const [selectedCellIds, setSelectedCellIds] = useState<string[]>([]);
  
  // Template Storage State
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savedTemplates, setSavedTemplates] = useState<TitleBlockTemplate[]>([]);

  // Refresh templates when load dialog opens
  useEffect(() => {
    if (loadDialogOpen) {
      const globalTemplates = TitleBlockStorage.getTemplates();
      // Combine global templates with project templates, avoiding duplicates
      // Priority: Project templates override global ones if IDs match (or just merge list)
      // Actually, let's just show all unique ones.
      const map = new Map<string, TitleBlockTemplate>();
      
      globalTemplates.forEach(t => map.set(t.id, t));
      projectTemplates.forEach(t => map.set(t.id, t)); // Project templates overwrite global if same ID
      
      setSavedTemplates(Array.from(map.values()));
    }
  }, [loadDialogOpen, projectTemplates]);

  const handleSaveTemplate = () => {
    if (!templateName.trim()) {
        toast.error('Bitte Namen eingeben');
        return;
    }
    const toSave: TitleBlockTemplate = {
      ...activeTemplate,
      id: 'custom-' + Date.now(),
      name: templateName,
      updatedAt: new Date().toISOString()
    };
    TitleBlockStorage.saveTemplate(toSave);
    if (onSaveProjectTemplate) {
        onSaveProjectTemplate(toSave);
    }
    setSaveDialogOpen(false);
    setTemplateName('');
    toast.success(t('editor.status.saved'));
  };

  const handleLoadTemplate = (t: TitleBlockTemplate) => {
    // Clone to ensure clean state
    const templateCopy = JSON.parse(JSON.stringify(t));
    onUpdatePlan(plan.id, { titleBlockTemplate: templateCopy });
    setLoadDialogOpen(false);
    toast.success('Plankopf geladen');
  };

  const handleDeleteTemplate = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Vorlage wirklich löschen?')) {
        TitleBlockStorage.deleteTemplate(id);
        if (onDeleteProjectTemplate) {
            onDeleteProjectTemplate(id);
        }
        setSavedTemplates(prev => prev.filter(t => t.id !== id));
        toast.success('Vorlage gelöscht');
    }
  };

  // Viewport Interaction State
  const [isViewportUnlocked, setIsViewportUnlocked] = useState(false);

  // Print Mode State
  const [isPrinting, setIsPrinting] = useState(false);
  
  // Debug Grid State
  const [showDebugGrid, setShowDebugGrid] = useState(false);

  useEffect(() => {
    const handleBeforePrint = () => {
      setIsPrinting(true);
      // Ensure UI is clean for printing
      setIsEditingTitleBlock(false);
      setIsViewportUnlocked(false);
      setSelectedCellIds([]);
    };
    const handleAfterPrint = () => {
      setIsPrinting(false);
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  // Drag Selection State
  const isSelectingRef = useRef(false);
  const selectionStartCellIdRef = useRef<string | null>(null);

  // Conversion: 1mm = 3.78px (approx 96 DPI)
  const PIXELS_PER_MM = 3.7795275591; 
  const paperWidthPx = plan.width * PIXELS_PER_MM;
  const paperHeightPx = plan.height * PIXELS_PER_MM;

  // Title Block Position State
  const defaultTitleX = plan.width - 190;
  const defaultTitleY = plan.height - 183;
  
  // Title Block Template & Data
  const activeTemplate = useMemo(() => {
    return plan.titleBlockTemplate || getFahnerTemplate();
  }, [plan.titleBlockTemplate]);

  const activeData = useMemo(() => {
    // Merge plan-specific data with some defaults/system vars that might not be in the map yet
    // In a real app, these "defaults" might come from the Project settings
    return {
      'project.name': projectName || '', // Ensure empty string if not set, instead of 'Projekt'
      'project.client': '',
      'project.number': '',
      'plan.content': '',
      'meta.drawnBy': 'User',
      'meta.date': new Date().toLocaleDateString(),
      ...plan.titleBlockData
    };
  }, [plan.titleBlockData, projectName]);

  const handlePaperDoubleClick = (e: React.MouseEvent) => {
    // Enter viewport edit mode
    if (!isEditingTitleBlock && !isViewportUnlocked) {
      setIsViewportUnlocked(true);
      toast.info(t.print.messages.viewportUnlocked);
    }
  };

  const handleTitleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingTitleBlock(true);
    // Ensure viewport is locked when editing title block
    if (isViewportUnlocked) setIsViewportUnlocked(false);
  };
  
  const handleTitleDataChange = (newData: any) => {
     onUpdatePlan(plan.id, { 
       titleBlockData: { ...plan.titleBlockData, ...newData } 
     });
  };

  const handleTitleCellChange = (cellId: string, value: string, type: 'text' | 'attribute') => {
      // Create a deep copy of template
      const newTemplate = JSON.parse(JSON.stringify(activeTemplate)) as TitleBlockTemplate;
      const cellIndex = newTemplate.cells.findIndex(c => c.id === cellId);
      if (cellIndex >= 0) {
        newTemplate.cells[cellIndex].value = value;
        newTemplate.cells[cellIndex].type = type;
        onUpdatePlan(plan.id, { titleBlockTemplate: newTemplate });
      }
  };

  const handleTemplateStyleUpdate = (updates: Partial<TitleBlockCellStyles>) => {
    if (selectedCellIds.length === 0) return;
    
    // Create a deep copy of template
    const newTemplate = JSON.parse(JSON.stringify(activeTemplate)) as TitleBlockTemplate;
    
    let changed = false;
    selectedCellIds.forEach(id => {
      const cellIndex = newTemplate.cells.findIndex(c => c.id === id);
      if (cellIndex >= 0) {
        const cell = newTemplate.cells[cellIndex];
        cell.styles = { ...cell.styles, ...updates };
        changed = true;
      }
    });
    
    if (changed) {
      onUpdatePlan(plan.id, { titleBlockTemplate: newTemplate });
    }
  };

  const handleCellUpdate = (updates: Partial<TitleBlockCell>) => {
    if (selectedCellIds.length === 0) return;
    
    const newTemplate = JSON.parse(JSON.stringify(activeTemplate)) as TitleBlockTemplate;
    
    let changed = false;
    selectedCellIds.forEach(id => {
      const cellIndex = newTemplate.cells.findIndex(c => c.id === id);
      if (cellIndex >= 0) {
        newTemplate.cells[cellIndex] = { ...newTemplate.cells[cellIndex], ...updates };
        changed = true;
      }
    });

    if (changed) {
      onUpdatePlan(plan.id, { titleBlockTemplate: newTemplate });
    }
  };

  const handleGridAction = (action: GridAction) => {
    if (selectedCellIds.length === 0) return;
    // For single-cell actions, use the first selected cell
    const primaryId = selectedCellIds[0];
    const cell = activeTemplate.cells.find(c => c.id === primaryId);
    if (!cell) return;

    let newTemplate = activeTemplate;

    switch (action.type) {
        case 'INSERT_ROW':
            newTemplate = insertRow(activeTemplate, action.after ? cell.rowIndex + 1 : cell.rowIndex);
            break;
        case 'DELETE_ROW':
            newTemplate = deleteRow(activeTemplate, cell.rowIndex);
            break;
        case 'INSERT_COL':
            newTemplate = insertCol(activeTemplate, action.after ? cell.colIndex + 1 : cell.colIndex);
            break;
        case 'DELETE_COL':
            newTemplate = deleteCol(activeTemplate, cell.colIndex);
            break;
        case 'MERGE':
            newTemplate = mergeCells(activeTemplate, primaryId, action.direction);
            break;
        case 'MERGE_SELECTION':
            newTemplate = mergeSelectedCells(activeTemplate, selectedCellIds);
            break;
        case 'SPLIT':
            // Split works on primary cell only? Or all selected?
            // Usually split is per cell. Let's do it for all selected cells individually.
            selectedCellIds.forEach(id => {
               newTemplate = splitCell(newTemplate, id);
            });
            break;
        case 'SET_BORDERS':
            newTemplate = setBorderMode(activeTemplate, selectedCellIds, action.mode, action.width);
            break;
    }

    if (newTemplate !== activeTemplate) {
        onUpdatePlan(plan.id, { titleBlockTemplate: newTemplate });
        // Selection might become invalid if cells merged/deleted.
        // For MERGE_SELECTION, the logic keeps the first cell ID, so selection on that one is valid.
        // But others are gone. Safe bet: reset selection to primary ID or clear if deleted.
        if (action.type === 'MERGE_SELECTION') {
            setSelectedCellIds([primaryId]);
        } else if (action.type === 'DELETE_ROW' || action.type === 'DELETE_COL') {
            setSelectedCellIds([]);
        }
    }
  };

  const selectedCells = activeTemplate.cells.filter(c => selectedCellIds.includes(c.id));

  // Clear selection when exiting edit mode
  React.useEffect(() => {
    if (!isEditingTitleBlock) {
      setSelectedCellIds([]);
    }
  }, [isEditingTitleBlock]);

  // Drag Selection Handlers
  const handleCellMouseDown = (cellId: string, e: React.MouseEvent) => {
    if (!isEditingTitleBlock) return;
    if (e.button !== 0) return; // Only left click
    
    isSelectingRef.current = true;
    selectionStartCellIdRef.current = cellId;
    setSelectedCellIds([cellId]);
  };

  const handleCellMouseEnter = (cellId: string, e: React.MouseEvent) => {
    if (!isSelectingRef.current || !selectionStartCellIdRef.current) return;
    
    const startId = selectionStartCellIdRef.current;
    const startCell = activeTemplate.cells.find(c => c.id === startId);
    const endCell = activeTemplate.cells.find(c => c.id === cellId);
    
    if (!startCell || !endCell) return;
    
    // Calculate rectangular range bounds
    // Range must cover the FULL span of start and end cells
    const startR = startCell.rowIndex;
    const startC = startCell.colIndex;
    const endR = endCell.rowIndex;
    const endC = endCell.colIndex;
    
    const minRow = Math.min(startR, endR);
    const maxRow = Math.max(startR + (startCell.rowSpan||1) - 1, endR + (endCell.rowSpan||1) - 1);
    const minCol = Math.min(startC, endC);
    const maxCol = Math.max(startC + (startCell.colSpan||1) - 1, endC + (endCell.colSpan||1) - 1);
    
    // Select all cells that intersect with this bounding box
    const newSelectedIds = activeTemplate.cells.filter(cell => {
      const cR = cell.rowIndex;
      const cC = cell.colIndex;
      const cREnd = cR + (cell.rowSpan || 1) - 1;
      const cCEnd = cC + (cell.colSpan || 1) - 1;
      
      // Check intersection
      return !(cREnd < minRow || cR > maxRow || cCEnd < minCol || cC > maxCol);
    }).map(c => c.id);
    
    setSelectedCellIds(newSelectedIds);
  };
  
  const handleCellMouseUp = () => {
     isSelectingRef.current = false;
     selectionStartCellIdRef.current = null;
  };

  // Global Keyboard Shortcuts (Escape to exit modes)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
       if (e.key === 'Escape') {
          if (isEditingTitleBlock) {
              setIsEditingTitleBlock(false);
              setSelectedCellIds([]);
          } else if (isViewportUnlocked) {
              setIsViewportUnlocked(false);
              toast.info("Viewport fixiert");
          }
       }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditingTitleBlock, isViewportUnlocked]);

  // Keyboard Handlers for Title Block Edit
  useEffect(() => {
    if (!isEditingTitleBlock) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedCellIds.length === 0) return;
        
        // Modifiers for structural delete
        // Shift + Del = Delete Row
        // Alt + Del = Delete Column
        if (e.shiftKey) {
           e.preventDefault();
           handleGridAction({ type: 'DELETE_ROW' });
        } else if (e.altKey) {
           e.preventDefault();
           handleGridAction({ type: 'DELETE_COL' });
        } else {
           // Default: Clear content
           handleCellUpdate({ value: '' });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditingTitleBlock, selectedCellIds, activeTemplate]);

  // If plan has saved position, use it. Otherwise default.
  // We use local state for smooth dragging, commit on mouse up.
  const [titlePos, setTitlePos] = useState(plan.titleBlockPosition || { x: defaultTitleX, y: defaultTitleY });

  // Sync local state when prop changes (external update), but only if not dragging
  React.useEffect(() => {
    if (dragModeRef.current !== 'title') {
      setTitlePos(plan.titleBlockPosition || { x: defaultTitleX, y: defaultTitleY });
    }
  }, [plan.titleBlockPosition, defaultTitleX, defaultTitleY]);


  // Calculate ViewState for the Canvas to match Plan Viewport
  const viewState: ViewState = useMemo(() => ({
    zoom: plan.viewport.scale,
    // Center the viewport point (centerX, centerY) at the center of the paper
    panX: (paperWidthPx / 2) - plan.viewport.centerX * plan.viewport.scale,
    panY: (paperHeightPx / 2) - plan.viewport.centerY * plan.viewport.scale,
  }), [plan.viewport, paperWidthPx, paperHeightPx]);

  // Use BlockSystem settings for WYSIWYG
  // If "Show Grid" checkbox is checked in Plan, we force visibility to true.
  // If unchecked, we set visible to false (showing only the container background).
  const printBackgroundSettings: BackgroundSettings = useMemo(() => ({
    ...backgroundSettings,
    visible: plan.showGrid,
    snapToGrid: false
  }), [plan.showGrid, backgroundSettings]);

  // Screen Pan/Drag Logic
  const handleMouseDown = (e: React.MouseEvent) => {
    // If viewport is unlocked, let the canvas handle interaction
    if (isViewportUnlocked) return;

    // Middle or Left click on background -> Pan
    // But check if we clicked title block first? 
    // Title block has its own handler that stops propagation.
    if (e.button === 1 || e.button === 0) { 
      dragModeRef.current = 'pan';
      lastMouseRef.current = { x: e.clientX, y: e.clientY };
      e.preventDefault();
    }
  };

  const handleTitleMouseDown = (e: React.MouseEvent) => {
    if (isEditingTitleBlock) return; // Disable move drag in edit mode

    if (e.button === 0) { // Left click only
      e.stopPropagation(); // Don't trigger pan
      dragModeRef.current = 'title';
      lastMouseRef.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragModeRef.current) return;

    const dx = e.clientX - lastMouseRef.current.x;
    const dy = e.clientY - lastMouseRef.current.y;
    lastMouseRef.current = { x: e.clientX, y: e.clientY };

    if (dragModeRef.current === 'pan') {
      setPan(p => ({ x: p.x + dx, y: p.y + dy }));
    } else if (dragModeRef.current === 'title') {
      // Convert screen pixels to mm
      // Zoom affects how many pixels represent a mm
      const dxMm = dx / zoom / PIXELS_PER_MM;
      const dyMm = dy / zoom / PIXELS_PER_MM;
      
      setTitlePos(p => ({ x: p.x + dxMm, y: p.y + dyMm }));
    }
  };

  const handleMouseUp = () => {
    if (dragModeRef.current === 'title') {
      // Commit the new position
      onUpdatePlan(plan.id, { titleBlockPosition: titlePos });
    }
    dragModeRef.current = null;
    
    // Also stop selection drag just in case mouse released outside cells
    isSelectingRef.current = false;
  };

  const handleWheel = (e: React.WheelEvent) => {
    // If viewport is unlocked, let the canvas handle zoom/pan of content
    if (isViewportUnlocked) return;

    if (e.ctrlKey) {
      // Zoom
      e.preventDefault();
      const scaleFactor = 1.1;
      if (e.deltaY < 0) {
        setZoom(z => Math.min(z * scaleFactor, 5));
      } else {
        setZoom(z => Math.max(z / scaleFactor, 0.1));
      }
    } else {
      // Pan
      setPan(p => ({ x: p.x - e.deltaX, y: p.y - e.deltaY }));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDxfExport = () => {
    try {
      exportPlanToDxf({
        plan,
        blocks,
        connections,
        textAnnotations,
        placedLabels,
        labelTemplates,
        titleBlockTemplate: activeTemplate,
        titleBlockData: activeData,
        titleBlockPosition: titlePos,
        projectName,
        gridSize: backgroundSettings.isometricGridSize,
        showDebugGrid,
        viewState
      });
      toast.success(t.print.messages.exportStarted);
    } catch (error) {
      console.error('DXF Export failed:', error);
      toast.error(t.print.messages.exportFailed);
    }
  };

  const handleCenterViewport = () => {
    if (blocks.length === 0) {
      toast.info(t.print.messages.noBlocks);
      return;
    }

    // Find bounding box of all blocks
    let minU = Infinity, maxU = -Infinity;
    let minV = Infinity, maxV = -Infinity;

    blocks.forEach(block => {
      block.blockData.elements.forEach(el => {
        if (el.type === 'line') {
          const start = { u: el.start.u + block.transform.position.u, v: el.start.v + block.transform.position.v };
          const end = { u: el.end.u + block.transform.position.u, v: el.end.v + block.transform.position.v };
          minU = Math.min(minU, start.u, end.u);
          maxU = Math.max(maxU, start.u, end.u);
          minV = Math.min(minV, start.v, end.v);
          maxV = Math.max(maxV, start.v, end.v);
        } else if (el.type === 'connection') {
          const pos = { u: el.position.u + block.transform.position.u, v: el.position.v + block.transform.position.v };
          minU = Math.min(minU, pos.u);
          maxU = Math.max(maxU, pos.u);
          minV = Math.min(minV, pos.v);
          maxV = Math.max(maxV, pos.v);
        }
      });
    });

    if (!isFinite(minU) || !isFinite(maxU) || !isFinite(minV) || !isFinite(maxV)) {
      toast.error(t.print.messages.invalidCoords);
      return;
    }

    // Convert corners to screen coordinates to find center
    const gridSize = backgroundSettings.isometricGridSize;
    const corners = [
      { u: minU, v: minV },
      { u: maxU, v: minV },
      { u: maxU, v: maxV },
      { u: minU, v: maxV },
    ];

    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    corners.forEach(p => {
      const screenP = isoToScreen(p, gridSize);
      minX = Math.min(minX, screenP.x);
      maxX = Math.max(maxX, screenP.x);
      minY = Math.min(minY, screenP.y);
      maxY = Math.max(maxY, screenP.y);
    });

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    onUpdatePlan(plan.id, {
      viewport: {
        ...plan.viewport,
        centerX,
        centerY
      }
    });

    toast.success(t.print.messages.viewCentered);
  };

  const handleViewportChange = (newViewState: ViewState) => {
    // Convert Screen Pan/Zoom back to Plan Viewport Center/Scale
    // Formula: panX = (paperWidthPx / 2) - centerX * scale
    // Therefore: centerX = ((paperWidthPx / 2) - panX) / scale
    
    const newScale = newViewState.zoom;
    if (newScale <= 0.0001) return;

    const newCenterX = (paperWidthPx / 2 - newViewState.panX) / newScale;
    const newCenterY = (paperHeightPx / 2 - newViewState.panY) / newScale;

    onUpdatePlan(plan.id, {
      viewport: {
        ...plan.viewport,
        scale: newScale,
        centerX: newCenterX,
        centerY: newCenterY
      }
    });
  };

  // No-op handlers for read-only canvas
  const noop = () => {};
  const noopUpdate = () => {};
  const noopSelect = () => {};

  return (
    <div 
      className="absolute inset-0 bg-zinc-800 overflow-hidden cursor-grab active:cursor-grabbing z-0 print:p-0 print:bg-white print:overflow-visible print:static"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
      style={{ touchAction: 'none' }}
      id="plan-layout-editor-root"
    >
      <style>{`
        @media print {
          @page {
            size: ${plan.width}mm ${plan.height}mm;
            margin: 0;
          }
          html, body {
            width: ${plan.width}mm;
            height: ${plan.height}mm;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          body * {
            visibility: hidden;
          }
          
          /* Root container settings - Use fixed positioning to force top-left anchor */
          #plan-layout-editor-root {
            visibility: visible !important;
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100% !important;
            height: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            z-index: 9999 !important;
            transform: none !important;
          }
          
          #plan-layout-editor-root * {
            visibility: visible !important;
          }
          
          /* The paper container itself */
          #plan-paper-container {
            visibility: visible !important;
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            transform: none !important; /* Reset any screen-based scaling/translation/centering */
            width: ${plan.width}mm !important; /* Force exact mm dimensions */
            height: ${plan.height}mm !important;
            border: none !important;
            box-shadow: none !important;
            overflow: hidden !important;
          }
          
          /* Hide UI elements specifically */
          .no-print, .print\\:hidden {
            display: none !important;
          }
          #plan-ui-hud, #plan-ui-toolbar {
            display: none !important;
          }
        }
      `}</style>
      
      <div 
        id="plan-paper-container"
        className="shadow-xl relative print:shadow-none"
        onDoubleClick={handlePaperDoubleClick}
        style={{
          width: paperWidthPx,
          height: paperHeightPx,
          backgroundColor: backgroundSettings.backgroundColor || '#ffffff', // Use system background color
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: 'center center',
          transition: dragModeRef.current === 'pan' ? 'none' : 'transform 0.1s ease-out',
          margin: 'auto', // Center in container initially
          top: '50%',
          left: '50%',
          marginTop: -paperHeightPx / 2,
          marginLeft: -paperWidthPx / 2,
        }}
      >
        {/* Layer 1: Drawing Canvas */}
        <div className={`absolute inset-0 z-0 ${isViewportUnlocked ? 'pointer-events-auto ring-2 ring-blue-500' : 'pointer-events-none'}`}>
          <div className="absolute inset-0" style={{ clipPath: 'inset(5mm)' }}>
            <BlockSystemCanvas
              blocks={blocks}
              connections={connections}
              connectionMarkers={connectionMarkers || []}
              selectedBlockIds={[]}
              selectedConnectionIds={[]}
              selectedMarkerIds={[]}
              textAnnotations={textAnnotations}
              selectedTextIds={[]}
              labelTemplates={labelTemplates}
              placedLabels={placedLabels}
              selectedLabelIds={[]}
              systems={systems}
              pipeTypes={pipeTypes}
              projectName={projectName}
              viewState={viewState}
              backgroundSettings={printBackgroundSettings}
              viewDisplaySettings={viewDisplaySettings}
              systemColors={systemColors}
              snapEnabled={false}
              showDimensions={true}
              isPrintMode={isPrinting}
              
              // Interaction Handlers (Disabled unless unlocked)
              onViewStateChange={isViewportUnlocked ? handleViewportChange : noop}
              onBlockAdd={noop}
              onBlockUpdate={noopUpdate}
              onBlockSelect={noopSelect}
              onConnectionSelect={noopSelect}
              onMarkerSelect={noopSelect}
              onTextSelect={noopSelect}
              onTextClick={noop}
              onTextEdit={noop}
              onTextUpdate={noopUpdate}
              onLabelSelect={noopSelect}
              onLabelAdd={noop}
              onLabelUpdate={noopUpdate}
              onLabelEdit={noop}
              onConnectionCreate={noop}
              onBlockAddWithConnection={noop}
              onDimensionClick={noop}
            />
          </div>
        </div>

        {/* Layer 2: Title Block Overlay */}
        <div className="absolute inset-0 z-10 pointer-events-none">
           <svg 
             width="100%" 
             height="100%" 
             viewBox={`0 0 ${plan.width} ${plan.height}`}
             preserveAspectRatio="xMidYMid meet"
           >
              {/* Drawing Frame (Rahmen) - Outer Frame REMOVED */}

              {/* Drawing Frame - 5mm Margin */}
              <rect 
                x="5" 
                y="5" 
                width={plan.width - 10} 
                height={plan.height - 10} 
                fill="none" 
                stroke="black" 
                strokeWidth="0.35" 
              />

              {/* Faltmarkierungen (Fold Marks) for A3 */}
              {/* Positioned at bottom frame edge, extending to paper edge */}
              {plan.showCropMarks && plan.width === 420 && (
                <g stroke="black" strokeWidth="0.35">
                   {/* 1. Left Heftrand Mark (190mm from left) */}
                   <line x1="190" y1={plan.height} x2="190" y2={plan.height - 5} />
                   
                   {/* 2. Center Mark (210mm from left) */}
                   <line x1="210" y1={plan.height} x2="210" y2={plan.height - 5} />
                   
                   {/* 3. Right Heftrand Mark (230mm from left) */}
                   <line x1="230" y1={plan.height} x2="230" y2={plan.height - 5} />
                </g>
              )}

              {/* Lochmarke (Punch Mark) - Left Center */}
              {plan.showCropMarks && (
                 <line x1="0" y1={plan.height / 2} x2="5" y2={plan.height / 2} stroke="black" strokeWidth="0.35" />
              )}

              {/* Title Block Positioned Bottom Right aligned to Frame */}
              <g 
                transform={`translate(${titlePos.x}, ${titlePos.y})`}
                onMouseDown={handleTitleMouseDown}
                onDoubleClick={handleTitleDoubleClick}
                style={{ cursor: isEditingTitleBlock ? 'default' : 'move', pointerEvents: 'all' }}
                className="hover:opacity-80 transition-opacity"
              >
                {/* Transparent hit area is handled inside DynamicTitleBlock per cell, 
                    but we might want a global hit area for the drag if clicking empty spots?
                    DynamicTitleBlock renders cells covering the area, so it should be fine.
                */}
                <DynamicTitleBlock 
                  template={activeTemplate} 
                  data={activeData}
                  plan={plan}
                  showPlaceholders={isEditingTitleBlock}
                  selectedCellIds={selectedCellIds}
                  onCellClick={isEditingTitleBlock ? (id, e) => {
                     // If Ctrl is pressed, toggle selection
                     if (e.ctrlKey) {
                        if (selectedCellIds.includes(id)) {
                           setSelectedCellIds(ids => ids.filter(i => i !== id));
                        } else {
                           setSelectedCellIds(ids => [...ids, id]);
                        }
                     } else {
                        // Regular click is handled by MouseDown for drag start
                        // But if we just click without drag, we ensure selection is set
                        // Handled by MouseDown already
                     }
                  } : undefined}
                  onCellMouseDown={isEditingTitleBlock ? handleCellMouseDown : undefined}
                  onCellMouseEnter={isEditingTitleBlock ? handleCellMouseEnter : undefined}
                  onCellMouseUp={isEditingTitleBlock ? handleCellMouseUp : undefined}
                  onCellChange={handleTitleCellChange}
                  onDataChange={isEditingTitleBlock ? handleTitleDataChange : undefined}
                  onLayoutChange={(newCols, newRows) => {
                     // Create new template with updated dimensions
                     const newTemplate = {
                         ...activeTemplate,
                         columns: newCols,
                         rows: newRows
                     };
                     onUpdatePlan(plan.id, { titleBlockTemplate: newTemplate });
                  }}
                  uiZoom={zoom}
                />
              </g>
           </svg>
        </div>

        {/* Layer 2.5: Debug Grid Overlay */}
        {showDebugGrid && (
            <DebugGridOverlay width={plan.width} height={plan.height} />
        )}

        {/* Layer 3: Overlay UI (Exit Edit Mode + Toolbar) */}
        {isEditingTitleBlock && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2 w-full pointer-events-none">
            <div className="bg-black/80 text-white px-4 py-2 rounded-full text-sm shadow-xl flex items-center gap-2 max-w-[95vw] overflow-x-auto no-scrollbar pointer-events-auto">
               <span className="mr-2">{t.print.messages.titleBlockActive}</span>
               
               <Button variant="ghost" size="sm" className="h-6 text-xs hover:bg-white/20 text-white" onClick={() => setLoadDialogOpen(true)}>
                   <FolderOpen className="w-3 h-3 mr-1" /> {t.print.messages.load}
               </Button>
               <Button variant="ghost" size="sm" className="h-6 text-xs hover:bg-white/20 text-white" onClick={() => { setTemplateName(activeTemplate.name || ''); setSaveDialogOpen(true); }}>
                   <Save className="w-3 h-3 mr-1" /> {t.print.messages.save}
               </Button>
               
               <div className="h-4 w-px bg-white/30 mx-1" />

               <Button 
                 size="sm" 
                 variant="secondary" 
                 className="h-6 text-xs"
                 onClick={() => setIsEditingTitleBlock(false)}
               >
                 {t.print.messages.done}
               </Button>
            </div>
            
            <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t.print.messages.saveTemplateTitle}</DialogTitle>
                        <DialogDescription>
                            {t.print.messages.saveTemplateDesc}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="name" className="text-right">{t.attributes.name}</Label>
                            <Input id="name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} className="col-span-3" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleSaveTemplate}>{t.print.messages.save}</Button>
                    </DialogFooter>
                </DialogContent>
             </Dialog>

             <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t.print.messages.loadTemplateTitle}</DialogTitle>
                        <DialogDescription>
                            {t.print.messages.loadTemplateDesc}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
                            {savedTemplates.map(t => (
                                <div key={t.id} className="flex items-center justify-between p-2 border rounded hover:bg-gray-50 cursor-pointer" onClick={() => handleLoadTemplate(t)}>
                                    <div className="flex flex-col">
                                        <span className="font-medium text-sm">{t.name}</span>
                                        <span className="text-xs text-gray-500">{new Date(t.updatedAt).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {t.id.startsWith('custom-') && (
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" onClick={(e) => handleDeleteTemplate(t.id, e)}>
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {savedTemplates.length === 0 && <div className="text-center text-gray-500 py-4">{t.print.messages.noTemplates}</div>}
                        </div>
                    </div>
                </DialogContent>
             </Dialog>
            
            {/* Context Toolbar */}
            {selectedCells.length > 0 && (
              <div className="flex flex-col items-center gap-1 w-full pointer-events-none">
                <div className="max-w-[95vw] overflow-x-auto no-scrollbar p-1 pointer-events-auto">
                  <TitleBlockToolbar 
                    selectedCells={selectedCells}
                    onUpdateStyle={handleTemplateStyleUpdate}
                    onUpdateCell={handleCellUpdate}
                    onGridAction={handleGridAction}
                  />
                </div>
                <div className="text-[10px] text-gray-500 bg-white/90 px-2 py-1 rounded shadow border border-gray-200">
                  {t.print.messages.shortcuts}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* HUD Controls */}
      <div id="plan-ui-hud" className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-white p-2 rounded-lg shadow-lg z-20 max-w-[95vw] overflow-x-auto no-scrollbar">
        <Button variant="ghost" size="icon" onClick={() => setZoom(z => Math.max(z - 0.1, 0.1))}>
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="text-sm w-12 text-center">{Math.round(zoom * 100)}%</span>
        <Button variant="ghost" size="icon" onClick={() => setZoom(z => Math.min(z + 0.1, 5))}>
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={handleCenterViewport} title={t.print.messages.centerView}>
          <Focus className="w-4 h-4" />
        </Button>
        <Button 
          variant={isViewportUnlocked ? "secondary" : "ghost"} 
          size="icon" 
          onClick={() => setIsViewportUnlocked(!isViewportUnlocked)} 
          title={isViewportUnlocked ? t.print.messages.viewportLocked : t.print.messages.viewportUnlocked}
        >
          {isViewportUnlocked ? <Unlock className="w-4 h-4 text-blue-600" /> : <Lock className="w-4 h-4" />}
        </Button>
        
        <div className="w-px h-4 bg-gray-300 mx-2" />
        <Button size="sm" onClick={handlePrint} className="gap-2">
          <Printer className="w-4 h-4" />
          {t.print.print}
        </Button>
        <Button size="sm" onClick={handleDxfExport} className="gap-2" variant="secondary">
          <Download className="w-4 h-4" />
          {t.print.dxf}
        </Button>
      </div>

      {/* Close Button (Top Right) */}
      <div className="absolute top-4 right-4 z-50 print:hidden">
        <Button 
          variant="secondary" 
          size="icon" 
          onClick={onClose} 
          className="shadow-lg hover:bg-red-500 hover:text-white transition-colors bg-white"
          title={t.common.close}
        >
          <X className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}
